import "./SpotDescription.scss";

import React, { useRef } from "react";
import moment, { Moment } from "moment";

import { Carrousel } from "../../Carrousel";
import HtmlRenderer from "../../HtmlRenderer";
import { useLanguage } from "../../hooks/useLanguage";
import windIcon from "../../Icons/windArrowSmall.svg";

const labels = {
  coastType: {
    da: "Kysttype",
    de: "Küstentyp",
    en: "Coastal type",
  },
  fishTypes: {
    da: "Fiskearter",
    de: "Fischarten",
    en: "Fish species",
  },
  windDirection: {
    da: "Optimal vindretning",
    de: "Optimale Windrichtung",
    en: "Optimal wind direction",
  },
  season: {
    da: "Sæson",
    de: "Saison",
    en: "Season",
  },
  months: {
    da: [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "Maj",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Okt",
      "Nov",
      "Dec",
    ],
    de: [
      "Jan",
      "Feb",
      "Mär",
      "Apr",
      "Mai",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Okt",
      "Nov",
      "Dez",
    ],
    en: [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ],
  },
  days: {
    da: [
      "Mandag",
      "Tirsdag",
      "Onsdag",
      "Torsdag",
      "Fredag",
      "Lørdag",
      "Søndag",
    ],
    en: [
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
      "Sunday",
    ],
    de: [
      "Montag",
      "Dienstag",
      "Mittwoch",
      "Donnerstag",
      "Freitag",
      "Samstag",
      "Sonntag",
    ],
  },
  winddirs: {
    da: ["N", "NØ", "Ø", "SØ", "S", "SV", "V", "NV"],
    en: ["N", "NE", "E", "SE", "S", "SW", "W", "NW"],
    de: ["N", "NO", "O", "SO", "S", "SW", "W", "NW"],
  },
  seasons: {
    high: {
      da: "Højsæson",
      de: "Hochsaison",
      en: "High season",
    },
    middle: {
      da: "Mellemsæson",
      de: "Zwischensaison",
      en: "Mid season",
    },
    low: {
      da: "Lavsæson",
      de: "Nebensaison",
      en: "Low season",
    },
    protected: {
      da: "Fredet",
      de: "Geschützt",
      en: "Protected",
    },
  },
  navgigateTo: {
    da: "Naviger til fiskeplads",
    de: "Zum Angelplatz navigieren",
    en: "Navigate to fishing spot",
  },
  weather: {
    current: {
      da: "Strøm",
      de: "Strom",
      en: "Current",
    },
    waterTemp: {
      da: "Vandtemperatur",
      de: "Wassertemperatur",
      en: "Water temperature",
    },
    seaLevel: {
      da: "Vandstand",
      de: "Wasserstand",
      en: "Sea level",
    },
    temp: {
      da: "Lufttemperatur",
      de: "Lufttemperatur",
      en: "Air temperature",
    },
    wind: {
      da: "Vind",
      de: "Wind",
      en: "Wind",
    },
    time: {
      da: "Tidspunkt",
      de: "Zeitpunkt",
      en: "Time",
    },
  },
};

/**
 * A component for displaying a spot description
 */

const SpotDescription = (props) => {
  const language = useLanguage();

  const isHigh = (index) => {
    return props.spot.seasons.some((s) => {
      let date = moment("14/" + (index + 1), "DD/MM");
      let start = moment(s.start, "DD/MM").year(date.year());
      let end = moment(s.end, "DD/MM").year(date.year());

      if (end.isAfter(start)) {
        return date.isBetween(start, end) && s.type === "high";
      } else {
        return !date.isBetween(end, start) && s.type === "high";
      }
    });
  };
  const isMiddle = (index) => {
    return props.spot.seasons.some((s) => {
      let date = moment("14/" + (index + 1), "DD/MM");
      let start = moment(s.start, "DD/MM").year(date.year());
      let end = moment(s.end, "DD/MM").year(date.year());

      if (end.isAfter(start)) {
        return date.isBetween(start, end) && s.type === "middle";
      } else {
        return !date.isBetween(end, start) && s.type === "middle";
      }
    });
  };
  const isProtected = (index) => {
    return props.spot.seasons.some((s) => {
      let date = moment("14/" + (index + 1), "DD/MM");
      let start = moment(s.start, "DD/MM").year(date.year());
      let end = moment(s.end, "DD/MM").year(date.year());

      if (end.isAfter(start)) {
        return date.isBetween(start, end) && s.type === "protected";
      } else {
        return !date.isBetween(end, start) && s.type === "protected";
      }
    });
  };
  const isFirstMonthProtected = (index) => {
    return props.spot.seasons.some((s) => {
      let date = moment("14/" + (index + 1), "DD/MM");
      let start = moment(s.start, "DD/MM").year(date.year());
      return date.month() === start.month() && s.type === "protected";
    });
  };
  const isLastMonthProtected = (index) => {
    return props.spot.seasons.some((s) => {
      let date = moment("14/" + (index + 1), "DD/MM");
      let end = moment(s.end, "DD/MM").year(date.year());
      return date.month() === end.month() && s.type === "protected";
    });
  };

  const navigateTo = (el) => {
    window.open(
      "https://www.google.com/maps/dir/?api=1&destination=" +
        el.gps[0].lat +
        "," +
        el.gps[0].long
    );
  };

  const videoSrc =
    props.spot["video" + (language === "da" ? "" : "_" + language)];

  const leftMarginNow = ((moment().dayOfYear() - 1) / 365) * 100 + "%";

  const windDirections = props.spot.wind.map((w) => (
    <span key={w}>
      {labels.winddirs[language][w - 1]}{" "}
      <img
        className="windIcon"
        src={windIcon}
        alt="Vind icon"
        style={{ transform: "rotate(" + (135 + (w - 1) * 45) + "deg)" }}
      />{" "}
    </span>
  ));

  const fishTypes = props.spot.fishTypes[language].map((f) => (
    <span key={f}>{f} </span>
  ));
  // console.log(props.spot);

  const months = labels.months[language].map((m, i) => {
    let cName = "month";
    cName += isHigh(i) ? " high" : "";
    cName += isMiddle(i) ? " middle" : "";
    cName += isProtected(i) && !isFirstMonthProtected(i) ? " protected" : "";
    // console.log(cName);
    return (
      <div key={m} className={cName}>
        <div>{m}</div>
        {isFirstMonthProtected(i) && <div className="protecFirstMonth" />}
        {isLastMonthProtected(i) && <div className="protecLastMonth" />}
      </div>
    );
  });

  return (
    <div style={{ position: "relative", height: "100%", width: "100%" }}>
      <div id="spotDescription">
        <div className="header">
          <div>
            <h2>{props.spot.name}</h2>
          </div>

          {props.spot.weather && (
            <WeatherWidget
              weather={props.spot.weather}
              currentTime={props.currentTime}
            />
          )}

          <Carrousel images={props.spot.images} />
          <div>
            <b>{labels.season[language]}</b>
          </div>
          <div className="seasonbar">
            <div id="seasonContainer">
              {months}
              <div className="now" style={{ left: leftMarginNow }} />
            </div>
          </div>
          <div
            id="seasonDescription"
            style={{
              marginBottom: 20,
            }}
          >
            <div>
              <div>
                <span style={{ background: "#558857" }} />
                {labels.seasons.high[language]}
              </div>
              <div>
                <span style={{ background: "#88a079" }} />
                {labels.seasons.middle[language]}
              </div>
            </div>
            <div>
              <div>
                <span style={{ background: "rgba(0,0,0,0.30)" }} />
                {labels.seasons.low[language]}
              </div>
              <div>
                <span style={{ background: "rgba(234, 105, 105, 1)" }} />
                {labels.seasons.protected[language]}
              </div>
            </div>
          </div>
          <p
            className="description"
            style={{
              whiteSpace: "pre-wrap",
            }}
          >
            <HtmlRenderer html={props.spot.description[language]} />
          </p>
          <div className="headlineBar">
            <span className="label">{labels.coastType[language]}</span>
            <span className="data">{props.spot.coastType[language]}</span>
          </div>
          <div className="headlineBar">
            <span className="label">{labels.windDirection[language]}</span>
            <span className="directions data" data-bind="foreach: spot().wind">
              {windDirections}
            </span>
          </div>
          <div className="fishTypes" id="fiskeTyper">
            <span className="label">{labels.fishTypes[language]}</span>
            <span className="data">{fishTypes}</span>
          </div>
        </div>

        {/* <div><h4>Beskrivelse af fiskepladsen</h4></div> */}

        {props.spot.video !== "" && (
          <VideoPlayer key={props.spot.name} src={videoSrc} />
        )}
      </div>

      <div className="buttonContainer">
        <button className="navButton" onClick={() => navigateTo(props.spot)}>
          {labels.navgigateTo[language]}
        </button>
      </div>
    </div>
  );
};

export function VideoPlayer(props) {
  const videoPlayerRef = useRef(null);

  const handleFirstPlay = () => {
    // start from beginning
    videoPlayerRef.current.currentTime = 0;
    videoPlayerRef.current.play();
    videoPlayerRef.current.removeEventListener("play", handleFirstPlay);
  };

  return (
    <div
      style={{
        marginBottom: 20,
        position: "relative",
        zIndex: 2000,
      }}
    >
      <video
        ref={videoPlayerRef}
        style={{ width: "100%" }}
        controls
        onClick={() => {
          videoPlayerRef.current.paused
            ? videoPlayerRef.current.play()
            : videoPlayerRef.current.pause();
        }}
        onPlay={handleFirstPlay}
      >
        <source src={props.src + "#t=2"} type="video/mp4" />
        Your browser does not support the video tag.
      </video>
    </div>
  );
}

export default SpotDescription;

const WeatherWidget = (props: { weather: any; currentTime: Moment }) => {
  const language = useLanguage();
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(2, 1fr)",
        columnGap: 10,
      }}
    >
      <div className="headlineBar">
        <div className="label">{labels["weather"]["time"][language]}</div>
        <div className="data">
          {labels["days"][language][props.currentTime.day() - 1]}{" "}
          {props.currentTime.format("HH:mm")}
        </div>
      </div>
      <div className="headlineBar">
        <div className="label">{labels["weather"]["current"][language]}</div>
        <div className="data" style={{ display: "flex", alignItems: "center" }}>
          <svg
            style={{
              transform: `rotate(${props.weather.current.direction}deg)`,
              width: "24px",
              height: "24px",
              color: "white",
            }}
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="currentColor"
          >
            <path d="M16.1716 10.9999L10.8076 5.63589L12.2218 4.22168L20 11.9999L12.2218 19.778L10.8076 18.3638L16.1716 12.9999H4V10.9999H16.1716Z" />
          </svg>
          <div style={{ marginLeft: 10 }}>
            {props.weather.current.speed} m/s
          </div>
        </div>
      </div>
      <div className="headlineBar">
        <div className="label">{labels["weather"]["waterTemp"][language]}</div>
        <div className="data">{props.weather.waterTemp}°C</div>
      </div>
      <div className="headlineBar">
        <div className="label">{labels["weather"]["seaLevel"][language]}</div>
        {props.weather.seaLevel !== null && (
          <div className="data">
            <span className="mr-1">{props.weather.seaLevel >= 0 && "+"}</span>
            <span>{props.weather.seaLevel} m</span>
          </div>
        )}
      </div>
      <div className="headlineBar">
        <div className="label">{labels["weather"]["temp"][language]}</div>
        <div className="data">{props.weather.temp}°C</div>
      </div>
      <div className="headlineBar">
        <div className="label">{labels["weather"]["wind"][language]}</div>
        <div className="data" style={{ display: "flex", alignItems: "center" }}>
          <svg
            style={{
              transform: `rotate(${props.weather.windDirection + 90}deg)`,
              width: "24px",
              height: "24px",
              color: "white",
            }}
            fill="currentColor"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
          >
            <path d="M16.1716 10.9999L10.8076 5.63589L12.2218 4.22168L20 11.9999L12.2218 19.778L10.8076 18.3638L16.1716 12.9999H4V10.9999H16.1716Z" />
          </svg>
          <div style={{ marginLeft: 10 }}>{props.weather.windSpeed} m/s</div>
        </div>
      </div>
    </div>
  );
};
